// Client-side mirror of the parts of core-platform's password policy
// that can be checked instantly (internal/auth/password_policy.go,
// NIST SP 800-63B rev. 4 §3.1.1.2):
//
//   - at least 15 characters, counted as Unicode code points (as the
//     server counts them with utf8.RuneCountInString), and
//   - at most 72 bytes of UTF-8 (bcrypt's input limit).
//
// Everything else (the common-password blocklist, "not your email",
// one repeated character, control characters) is left to the server,
// whose `password_policy` `reason` the form shows verbatim through
// PASSWORD_POLICY_MESSAGE. There are deliberately no composition rules:
// the standard forbids them.

export const PASSWORD_MIN_CHARS = 15;
export const PASSWORD_MAX_BYTES = 72;

const encoder = new TextEncoder();

/** Unicode code points, not UTF-16 units: "😀" is one character. */
export function passwordChars(password: string): number {
  return Array.from(password).length;
}

export function passwordBytes(password: string): number {
  return encoder.encode(password).length;
}

export type LocalPasswordProblem = "too_short" | "too_long";

/** The server reason code this password would certainly get, or null if only the server can tell. */
export function checkPasswordLocally(password: string): LocalPasswordProblem | null {
  if (passwordChars(password) < PASSWORD_MIN_CHARS) return "too_short";
  if (passwordBytes(password) > PASSWORD_MAX_BYTES) return "too_long";
  return null;
}

/** Live hint text under a new-password field. */
export function passwordMeter(password: string): string {
  const chars = passwordChars(password);
  const bytes = passwordBytes(password);
  if (bytes > PASSWORD_MAX_BYTES) return `Too long: ${bytes} of at most ${PASSWORD_MAX_BYTES} bytes.`;
  if (chars < PASSWORD_MIN_CHARS) return `${chars} of at least ${PASSWORD_MIN_CHARS} characters.`;
  // Only mention bytes once they are near the limit.
  if (bytes > PASSWORD_MAX_BYTES - 12) return `${chars} characters, ${bytes} of at most ${PASSWORD_MAX_BYTES} bytes.`;
  return `${chars} characters.`;
}
